/**
 * Money-page link layer for the programmatic guide library.
 *
 * 500+ guide pages each linked mostly to *other guides*, so the internal
 * PageRank they collect stayed trapped inside the guide cluster. Every guide
 * and guide article now spends its strongest internal links on the commercial
 * hubs instead, with keyword-matched anchor text and real server-rendered
 * <a href> anchors so crawlers follow them.
 *
 * The set is topic-aware: card/price guides feed the TCG hubs first, gameplay
 * guides lead with the in-game databases but still pass a link to the card
 * market so equity flows across the two categories.
 */

interface MoneyLink {
  href: string;
  label: string;
  blurb: string;
}

/** Commercial hubs, in priority order. These are the pages we want ranking. */
const TCG_LINKS: MoneyLink[] = [
  { href: "/prices", label: "Palworld TCG card prices", blurb: "Live market value for every printed card number." },
  { href: "/tcg-price-index", label: "Palworld TCG Price Index", blurb: "Benchmark showing whether the market is rising or falling." },
  { href: "/cards", label: "Palworld TCG card list", blurb: "Every official English printing with rarity and set." },
  { href: "/tcg-bulk-order", label: "Palworld TCG bulk card order", blurb: "Paste card numbers and buy the whole list in one checkout." },
  { href: "/merch", label: "Palworld merch and TCG products", blurb: "Sealed boxes, decks, plush and apparel prices." },
  { href: "/marketplace", label: "Palworld card marketplace", blurb: "Compare marketplaces before you buy a single." },
];

const GAME_LINKS: MoneyLink[] = [
  { href: "/pals", label: "Palworld Pal database", blurb: "Stats, drops, work suitability and locations." },
  { href: "/breeding", label: "Palworld breeding calculator", blurb: "Any two parents to the exact child egg." },
  { href: "/damage", label: "Palworld damage calculator", blurb: "Effective DPS and boss time-to-kill." },
  { href: "/mounts", label: "Palworld mount speed table", blurb: "Fly, ground and swim speeds ranked." },
  { href: "/prices", label: "Palworld TCG card prices", blurb: "Live market value for every printed card number." },
  { href: "/tcg-price-index", label: "Palworld TCG Price Index", blurb: "Is the card market rising or falling right now?" },
];

const TCG_HINTS = /card|tcg|deck|pack|price|grad|sealed|booster|trial|promo|merch|plush|apparel|invest|bulk|collect/i;

export function MoneyPageLinks({
  topic,
  heading = "Palworld data and price hubs",
  className = "mt-10",
}: {
  /** Guide title/tag/keywords — decides which hub set leads. */
  topic?: string;
  heading?: string;
  className?: string;
}) {
  const tcgFirst = TCG_HINTS.test(topic ?? "");
  const links = tcgFirst ? TCG_LINKS : GAME_LINKS;

  return (
    <nav aria-label={heading} className={`rounded-2xl border bg-card/40 p-6 ${className}`}>
      <h2 className="text-lg font-semibold">{heading}</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        The pages this guide draws its numbers from — updated daily and maintained by Pal Stars.
      </p>
      <ul className="mt-4 grid gap-3 sm:grid-cols-2">
        {links.map((l) => (
          <li key={l.href} className="rounded-lg border border-border/60 bg-background/40 p-3">
            <a href={l.href} className="text-sm font-semibold text-primary hover:underline">
              {l.label}
            </a>
            <p className="mt-1 text-xs text-muted-foreground">{l.blurb}</p>
          </li>
        ))}
      </ul>
    </nav>
  );
}
